/**
 * ThreadPanel - Thread view with virtualized reply list.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../contexts/ChatContext';
import { chatMessagesApi } from '../../services/chatApi';
import MessageItem from './MessageItem';
import MessageInput from './MessageInput';
import type { ChatMessage } from '../../types/chat';

const ThreadPanel: React.FC = () => {
  const { t } = useTranslation('chat');
  const { threadParentMessage, setThreadParentMessage } = useChat();
  const [replies, setReplies] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);

  const loadReplies = useCallback(async () => {
    if (!threadParentMessage) return;
    setLoading(true);
    try {
      const data = await chatMessagesApi.listReplies(threadParentMessage.id);
      setReplies(data);
    } catch (e) {
      console.error('Failed to load replies:', e);
    } finally {
      setLoading(false);
    }
  }, [threadParentMessage]);

  useEffect(() => {
    loadReplies();
  }, [loadReplies]);

  if (!threadParentMessage) return null;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-sm">{t('thread')}</h3>
        <button
          onClick={() => setThreadParentMessage(null)}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Parent message */}
      <div className="border-b bg-gray-50">
        <MessageItem message={threadParentMessage} />
      </div>

      {/* Replies */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : (
          replies.map(reply => (
            <MessageItem key={reply.id} message={reply} />
          ))
        )}
        {!loading && replies.length === 0 && (
          <div className="text-center text-gray-400 text-sm py-8">
            {t('noReplies')}
          </div>
        )}
      </div>

      {/* Reply input */}
      <MessageInput
        threadParentId={threadParentMessage.id}
        onMessageSent={loadReplies}
      />
    </div>
  );
};

export default ThreadPanel;
